import React, { useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CelebrationIcon from "@mui/icons-material/Celebration";
import "./DownloadCertificateModal.css";

const DownloadCertificateModal = ({
  open,
  onClose,
  userName,
  certificateImage,
  // onDownload,
}) => {
  const [copiedText, setCopiedText] = useState("");
  const [countdown, setCountdown] = useState(10);
  const [downloadStarted, setDownloadStarted] = useState(false);

  const linkedInPostText = `🎉 Excited to share that I've completed the Basic React.js Frontend Development certification from AceTechies Academy!

This certification has strengthened my skills in:
✅ React Components & Props
✅ State Management & Hooks
✅ React Router & Navigation
✅ Modern Frontend Development

I'm proud to be among the top 10% of developers who completed this program. Grateful for this learning journey and looking forward to applying these skills in real-world projects!

#ReactJS #FrontendDevelopment #WebDevelopment #JavaScript #Certification #TechSkills #AceTechies`;

  // Countdown timer effect
  useEffect(() => {
    if (open && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (open && countdown === 0 && !downloadStarted) {
      handleDownload();
    }
  }, [open, countdown, downloadStarted]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setCountdown(10);
      setDownloadStarted(false);
      setCopiedText("");
    }
  }, [open]);

  const handleDownload = async () => {
    try {
      setDownloadStarted(true);

      // Fetch the image and convert to a Blob
      const response = await fetch(certificateImage, { mode: "cors" });

      if (!response.ok) {
        throw new Error("Failed to fetch certificate image");
      }

      const blob = await response.blob();

      // Create a temporary URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${userName}_certificate.jpg`; // ensures direct download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      }, 1000);

      console.log("Certificate downloaded successfully");
    } catch (error) {
      console.error("Error downloading certificate:", error);
      alert(
        "Something went wrong while downloading your certificate. Please try again."
      );
    }
  };

  const handleCopyLinkedInPost = () => {
    navigator.clipboard.writeText(linkedInPostText);
    setCopiedText("copied");
    setTimeout(() => setCopiedText(""), 2000);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: "certificate-download-dialog",
      }}
    >
      <div className="certificate-download-content">
        <div className="certificate-dialog-close" onClick={onClose}>
          <CloseIcon />
        </div>

        <div className="certificate-congrats-icon">
          <CelebrationIcon sx={{ fontSize: 38 }} />
        </div>

        <h2 className="certificate-congrats-title">
          🎉 Congratulations, {userName}!
        </h2>

        <p className="certificate-congrats-subtitle">
          You've successfully earned your certificate in{" "}
          <strong>Basic React.js Frontend Development</strong>. You're in the
          top 10% of developers!
        </p>

        {/* Simple Countdown Timer Section */}
        <div className="certificate-countdown-section">
          {countdown > 0 ? (
            <div className="countdown-content">
              <p className="countdown-message">
                Your certificate will download in{" "}
                <span className="countdown-number">{countdown}</span>{" "}
                <span className="countdown-label">seconds</span>
              </p>
            </div>
          ) : (
            <div className="download-complete-message">
              <p className="download-status">✓ Download Started!</p>
              <p className="download-fallback">
                If download didn't start,{" "}
                <span className="download-manual-link" onClick={handleDownload}>
                  click here to download
                </span>
              </p>
            </div>
          )}
        </div>

        {/* LinkedIn Section */}
        <div className="certificate-linkedin-section">
          <div className="linkedin-header">
            <LinkedInIcon sx={{ fontSize: 28, color: "#0A66C2" }} />
            <h3>Share on LinkedIn</h3>
          </div>

          <div className="linkedin-post-container">
            <div className="linkedin-post-text">{linkedInPostText}</div>
            <button
              className="copy-post-button"
              onClick={handleCopyLinkedInPost}
            >
              {copiedText === "copied" ? (
                <span style={{ color: "#1a1a2e", fontWeight: "700" }}>
                  ✓ Copied!
                </span>
              ) : (
                <>
                  <ContentCopyIcon sx={{ fontSize: 18 }} />
                  <span>Copy Post</span>
                </>
              )}
            </button>
          </div>

          <div className="linkedin-instructions-box">
            <p className="instruction-title">
              📋 <strong>How to Share:</strong>
            </p>
            <ol className="instruction-list">
              <li>Copy the post text above</li>
              <li>Go to LinkedIn and create a new post</li>
              <li>Paste the text and attach your downloaded certificate</li>
              <li>
                Tag <strong>@AceTechies Academy</strong> and share!
              </li>
            </ol>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default DownloadCertificateModal;
